import { router } from "@inertiajs/react"
import {
  AlertTriangle,
  BookOpen,
  Check,
  ImageIcon,
  Loader2,
  Pencil,
  RotateCcw,
  Sparkles,
} from "lucide-react"
import { useCallback, useState } from "react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Skeleton } from "@/components/ui/skeleton"
import { useFlashcardProps } from "@/hooks/use-flashcard-props"
import AppLayout from "@/layouts/app-layout"
import { cn } from "@/lib/utils"
import type { ExtractedRow, StudyImport } from "@/types/study-import"

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getCsrfToken(): string {
  return (
    document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
      ?.content ?? ""
  )
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/** Inline-editable text cell. Click to edit, blur/enter to save. */
function EditableCell({
  value,
  onChange,
  className,
}: {
  value: string
  onChange: (v: string) => void
  className?: string
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value)

  const commit = () => {
    setEditing(false)
    if (draft.trim() !== value) onChange(draft.trim())
  }

  if (editing) {
    return (
      <Input
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") commit()
          if (e.key === "Escape") {
            setDraft(value)
            setEditing(false)
          }
        }}
        className={cn("h-7 px-2 text-sm", className)}
      />
    )
  }

  return (
    <button
      type="button"
      onClick={() => {
        setDraft(value)
        setEditing(true)
      }}
      className={cn(
        "group/cell flex min-h-[28px] w-full items-center gap-1.5 rounded px-2 py-1 text-left text-sm transition-colors hover:bg-slate-100 dark:hover:bg-slate-800",
        className,
      )}
    >
      <span className="flex-1 truncate">{value}</span>
      <Pencil className="size-3 shrink-0 text-slate-400 opacity-0 transition-opacity group-hover/cell:opacity-100" />
    </button>
  )
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface Props {
  studyImport: StudyImport
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function SelfStudyShow(props: unknown) {
  const { studyImport } = useFlashcardProps<Props>(props)

  // ── Local state derived from server data ──
  const [rows, setRows] = useState<ExtractedRow[]>(
    () => studyImport.extractedRows ?? [],
  )
  const [selected, setSelected] = useState<Set<number>>(
    () => new Set(rows.map((_, i) => i)),
  )
  const [isApproving, setIsApproving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const selectedCount = selected.size
  const allSelected = selectedCount === rows.length && rows.length > 0

  // ── Selection handlers ──

  const toggleRow = useCallback((index: number) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(index)) next.delete(index)
      else next.add(index)
      return next
    })
  }, [])

  const toggleAll = useCallback(() => {
    setSelected((prev) =>
      prev.size === rows.length
        ? new Set<number>()
        : new Set(rows.map((_, i) => i)),
    )
  }, [rows])

  // ── Row editing ──

  const updateRow = useCallback(
    (index: number, field: keyof ExtractedRow, value: string) => {
      setRows((prev) =>
        prev.map((r, i) => (i === index ? { ...r, [field]: value } : r)),
      )
    },
    [],
  )

  // ── Approve ──

  const handleApprove = async () => {
    if (selectedCount === 0) return
    setIsApproving(true)
    setError(null)

    const selectedRows = rows.filter((_, i) => selected.has(i))

    try {
      const res = await fetch(
        `/self-study/${studyImport.uuid}/approve`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": getCsrfToken(),
          },
          body: JSON.stringify({ selected_rows: selectedRows }),
        },
      )

      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as {
          error?: string
        } | null
        throw new Error(body?.error ?? `Failed (${res.status})`)
      }

      // Navigate to flashcards filtered by this import.
      // Background job creates flashcards & broadcasts via FlashcardChannel.
      router.visit(`/flashcards?study_import_id=${studyImport.uuid}`)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong")
      setIsApproving(false)
    }
  }

  // ── Status-based rendering ──

  if (studyImport.status === "processing") {
    return (
      <AppLayout
        breadcrumbs={[
          { title: "Dashboard", href: "/dashboard" },
          { title: "Self Study", href: "/self-study/new" },
          { title: "Processing", href: "#" },
        ]}
      >
        <div className="mx-auto max-w-2xl space-y-6 p-4 sm:p-6">
          <ProcessingSkeleton />
        </div>
      </AppLayout>
    )
  }

  if (studyImport.status === "failed") {
    return (
      <AppLayout
        breadcrumbs={[
          { title: "Dashboard", href: "/dashboard" },
          { title: "Self Study", href: "/self-study/new" },
          { title: "Failed", href: "#" },
        ]}
      >
        <div className="mx-auto max-w-2xl space-y-6 p-4 sm:p-6">
          <Card className="border-red-200 dark:border-red-800/40">
            <CardContent className="flex flex-col items-center gap-4 p-8 text-center">
              <div className="rounded-full bg-red-100 p-3 dark:bg-red-900/30">
                <AlertTriangle className="size-6 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                  Extraction Failed
                </h3>
                <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
                  We couldn&apos;t read vocabulary from this image. Try a
                  clearer photo with better lighting.
                </p>
              </div>
              <Button
                variant="outline"
                onClick={() => router.visit("/self-study/new")}
                className="gap-2"
              >
                <RotateCcw className="size-4" />
                Try Again
              </Button>
            </CardContent>
          </Card>
        </div>
      </AppLayout>
    )
  }

  if (studyImport.status === "completed") {
    return (
      <AppLayout
        breadcrumbs={[
          { title: "Dashboard", href: "/dashboard" },
          { title: "Self Study", href: "/self-study/new" },
          { title: "Completed", href: "#" },
        ]}
      >
        <div className="mx-auto max-w-2xl space-y-6 p-4 sm:p-6">
          <Card className="border-emerald-200 dark:border-emerald-800/40">
            <CardContent className="flex flex-col items-center gap-4 p-8 text-center">
              <div className="rounded-full bg-emerald-100 p-3 dark:bg-emerald-900/30">
                <Check className="size-6 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                  Flashcards Created
                </h3>
                <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
                  {studyImport.successfulCount} flashcards from this session are
                  ready to study.
                </p>
              </div>
              <Button
                onClick={() =>
                  router.visit(
                    `/flashcards?study_import_id=${studyImport.uuid}`,
                  )
                }
                className="gap-2"
              >
                <Sparkles className="size-4" />
                View Flashcards
              </Button>
            </CardContent>
          </Card>
        </div>
      </AppLayout>
    )
  }

  // ── Main: "reviewing" status ──

  return (
    <AppLayout
      breadcrumbs={[
        { title: "Dashboard", href: "/dashboard" },
        { title: "Self Study", href: "/self-study/new" },
        { title: "Review", href: "#" },
      ]}
    >
      <div className="mx-auto max-w-3xl space-y-5 p-4 sm:p-6">
        {/* ── Header ── */}
        <div
          className="animate-in fade-in slide-in-from-bottom-4 fill-mode-both flex items-start justify-between gap-4"
          style={{ animationDelay: "50ms" }}
        >
          <div className="flex items-center gap-3">
            <div className="rounded-xl bg-amber-100 p-2.5 dark:bg-amber-900/30">
              <BookOpen className="size-5 text-amber-700 dark:text-amber-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                Review Extracted Vocabulary
              </h2>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                {studyImport.bookTitle && (
                  <span className="mr-1.5 font-medium text-slate-700 dark:text-slate-300">
                    {studyImport.bookTitle} &middot;
                  </span>
                )}
                {rows.length} pairs found
                {studyImport.label && (
                  <Badge variant="secondary" className="ml-2 text-xs">
                    {studyImport.label}
                  </Badge>
                )}
              </p>
            </div>
          </div>

          {/* Source image thumbnail */}
          {studyImport.sourceImageUrl && (
            <img
              src={studyImport.sourceImageUrl}
              alt="Source page"
              className="size-14 shrink-0 rounded-lg border object-cover shadow-sm"
            />
          )}
        </div>

        {/* ── Results Table ── */}
        <Card
          className="animate-in fade-in slide-in-from-bottom-4 fill-mode-both overflow-hidden"
          style={{ animationDelay: "120ms" }}
        >
          <CardContent className="p-0">
            {/* Select-all header */}
            <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-700 dark:bg-slate-800/50">
              <label className="flex cursor-pointer items-center gap-3">
                <Checkbox
                  checked={allSelected}
                  onCheckedChange={toggleAll}
                  className="transition-all duration-150"
                />
                <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                  Select All
                </span>
              </label>
              <span className="text-xs font-medium text-slate-500 dark:text-slate-400">
                {selectedCount} of {rows.length} selected
              </span>
            </div>

            {/* Column headers */}
            <div className="grid grid-cols-[40px_1fr_1fr] items-center gap-0 border-b border-slate-200 bg-slate-50/50 px-4 py-2 dark:border-slate-700 dark:bg-slate-800/30">
              <span />
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                English
              </span>
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                Thai
              </span>
            </div>

            {/* Rows */}
            <div className="divide-y divide-slate-100 dark:divide-slate-800">
              {rows.map((row, index) => {
                const isSelected = selected.has(index)
                return (
                  <div
                    key={index}
                    className={cn(
                      "animate-in fade-in fill-mode-both grid grid-cols-[40px_1fr_1fr] items-center gap-0 px-4 py-2 transition-colors duration-150",
                      isSelected
                        ? "bg-white dark:bg-slate-900"
                        : "bg-slate-50/50 dark:bg-slate-800/20",
                    )}
                    style={{ animationDelay: `${180 + index * 40}ms` }}
                  >
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => toggleRow(index)}
                      className="transition-all duration-150"
                    />
                    <EditableCell
                      value={row.english}
                      onChange={(v) => updateRow(index, "english", v)}
                      className={cn(
                        !isSelected &&
                        "text-slate-400 line-through dark:text-slate-600",
                      )}
                    />
                    <EditableCell
                      value={row.thai}
                      onChange={(v) => updateRow(index, "thai", v)}
                      className={cn(
                        "text-base",
                        !isSelected &&
                        "text-slate-400 line-through dark:text-slate-600",
                      )}
                    />
                  </div>
                )
              })}
            </div>

            {rows.length === 0 && (
              <div className="flex flex-col items-center gap-2 p-8 text-center">
                <ImageIcon className="size-8 text-slate-300 dark:text-slate-600" />
                <p className="text-sm text-slate-500">
                  No vocabulary pairs were found in this image.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* ── Error ── */}
        {error && (
          <div className="animate-in fade-in rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800/40 dark:bg-red-950/20 dark:text-red-400">
            {error}
          </div>
        )}

        {/* ── Actions ── */}
        {rows.length > 0 && (
          <div
            className="animate-in fade-in slide-in-from-bottom-2 fill-mode-both flex gap-3"
            style={{ animationDelay: `${180 + rows.length * 40 + 80}ms` }}
          >
            <Button
              onClick={() => void handleApprove()}
              disabled={isApproving || selectedCount === 0}
              className="flex-1 gap-2"
              size="lg"
            >
              {isApproving ? (
                <>
                  <Loader2 className="size-4 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Sparkles className="size-4" />
                  Create {selectedCount}{" "}
                  {selectedCount === 1 ? "Flashcard" : "Flashcards"}
                </>
              )}
            </Button>
            <Button
              variant="outline"
              onClick={() => router.visit("/self-study/new")}
              disabled={isApproving}
            >
              Cancel
            </Button>
          </div>
        )}
      </div>
    </AppLayout>
  )
}

// ---------------------------------------------------------------------------
// Processing skeleton (reused for "processing" status)
// ---------------------------------------------------------------------------

const SKELETON_WIDTHS = [
  { left: 100, right: 70 },
  { left: 80, right: 55 },
  { left: 120, right: 65 },
  { left: 90, right: 80 },
  { left: 110, right: 50 },
] as const

function ProcessingSkeleton() {
  return (
    <Card className="overflow-hidden">
      <CardContent className="space-y-6 p-6">
        <div className="flex items-center gap-3">
          <Loader2 className="size-5 animate-spin text-amber-600" />
          <div>
            <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
              AI is reading your page...
            </p>
            <p className="text-xs text-slate-500">
              This usually takes a few seconds
            </p>
          </div>
        </div>

        <div className="space-y-0 rounded-lg border border-slate-200 dark:border-slate-700">
          <div className="flex gap-3 border-b border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-700 dark:bg-slate-800/50">
            <Skeleton className="h-4 w-20" />
            <div className="flex-1" />
            <Skeleton className="h-4 w-16" />
          </div>
          {SKELETON_WIDTHS.map((w, i) => (
            <div
              key={i}
              className="animate-in fade-in fill-mode-both flex items-center gap-3 border-b border-slate-100 px-4 py-3 last:border-b-0 dark:border-slate-800"
              style={{ animationDelay: `${200 + i * 120}ms` }}
            >
              <Skeleton className="size-4 rounded" />
              <Skeleton className="h-4" style={{ width: w.left }} />
              <div className="flex-1" />
              <Skeleton className="h-4" style={{ width: w.right }} />
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}
